import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Toast } from "primereact/toast";
import {
  FaPlus,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaYoutube,
  FaInstagram,
  FaFacebookSquare,
  FaLink,
  FaEye,
  FaClock,
  FaTrash,
  FaCopy,
  FaExternalLinkAlt,
  FaPlay,
  FaTh,
  FaList,
  FaInbox,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaMagic,
  FaLayerGroup,
  FaFire,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes olFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes olFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes olSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes olZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
@keyframes olBar { from { width: 0; } }
.ol-in { animation: olFadeUp .45s cubic-bezier(.2,.7,.2,1) both; }
.ol-fade { animation: olFade .2s ease both; }
.ol-slide { animation: olSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.ol-zoom { animation: olZoom .22s cubic-bezier(.2,.8,.2,1) both; }
.ol-bar { animation: olBar .8s cubic-bezier(.2,.7,.2,1) both; }
.ol-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.ol-press:hover { transform: translateY(-1px); }
.ol-press:active { transform: scale(.96); }
.ol-card { transition: transform .2s ease, box-shadow .25s ease, border-color .2s ease; }
.ol-card:hover { transform: translateY(-3px); box-shadow: 0 16px 30px -16px rgba(15,23,42,.35); }
.ol-card .ol-play { transition: transform .2s ease; }
.ol-card:hover .ol-play { transform: scale(1.12); }
.ol-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .ol-in, .ol-fade, .ol-slide, .ol-zoom, .ol-bar { animation: none; }
  .ol-press:hover, .ol-press:active, .ol-card:hover, .ol-card:hover .ol-play { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

/* ================= link types ================= */

const TYPES = [
  { value: "Youtube Video", short: "YT Video", icon: FaYoutube, text: "text-red-600", bg: "bg-red-50 dark:bg-red-900/30", solid: "bg-red-600", watch: 120 },
  { value: "Youtube Short", short: "YT Short", icon: FaYoutube, text: "text-red-500", bg: "bg-red-50 dark:bg-red-900/30", solid: "bg-red-500", watch: 30 },
  { value: "Instagram Reel", short: "IG Reel", icon: FaInstagram, text: "text-pink-600", bg: "bg-pink-50 dark:bg-pink-900/30", solid: "bg-pink-500", watch: 30 },
  { value: "Facebook Video", short: "FB Video", icon: FaFacebookSquare, text: "text-blue-700", bg: "bg-blue-50 dark:bg-blue-900/30", solid: "bg-blue-700", watch: null },
  { value: "Facebook Reel", short: "FB Reel", icon: FaFacebookSquare, text: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-900/30", solid: "bg-blue-600", watch: 30 },
];
const typeInfo = (v) =>
  TYPES.find((t) => t.value === v) || { value: v, short: v, icon: FaLink, text: "text-slate-500", bg: "bg-slate-100 dark:bg-slate-700", solid: "bg-slate-500" };

/* ================= helpers ================= */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";
const fmtWatch = (s) => {
  const n = Number(s || 0);
  if (!n) return "-";
  return n >= 60 ? `${Math.floor(n / 60)}m${n % 60 ? ` ${n % 60}s` : ""}` : `${n}s`;
};

const ytId = (url) => {
  const m = String(url || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};

// Guess the link type from the URL
const detectType = (url) => {
  const u = String(url || "").toLowerCase();
  if (/youtube\.com\/shorts\//.test(u)) return "Youtube Short";
  if (/youtu\.be\/|youtube\.com\//.test(u)) return "Youtube Video";
  if (/instagram\.com\/(reel|reels|p)\//.test(u)) return "Instagram Reel";
  if (/facebook\.com\/reel|fb\.watch\/.*reel|facebook\.com\/.*\/reels?\//.test(u)) return "Facebook Reel";
  if (/facebook\.com|fb\.watch/.test(u)) return "Facebook Video";
  return "";
};

// Check a URL fits the chosen type
const checkUrl = (url, type) => {
  const u = String(url || "").trim();
  if (!u) return "Enter a link";
  if (!/^https?:\/\//i.test(u)) return "Link must start with http:// or https://";
  if (type.startsWith("Youtube") && !ytId(u)) return "This is not a valid YouTube link";
  if (type === "Instagram Reel" && !/instagram\.com/i.test(u)) return "This is not an Instagram link";
  if (type.startsWith("Facebook") && !/facebook\.com|fb\.watch/i.test(u)) return "This is not a Facebook link";
  return "";
};

const normUrl = (u) => String(u || "").trim().replace(/\/+$/, "").toLowerCase();

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="ol-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Thumb = ({ link, className = "" }) => {
  const id = ytId(link.link_url);
  const t = typeInfo(link.link_type);
  const Icon = t.icon;
  const vertical = /Short|Reel/.test(link.link_type);
  return (
    <div className={`relative overflow-hidden bg-slate-900 ${className}`} style={{ aspectRatio: "16/9" }}>
      {id ? (
        <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt="" loading="lazy" className={`absolute inset-0 w-full h-full object-cover ${vertical ? "blur-sm scale-110 opacity-70" : ""}`} />
      ) : (
        <div className={`absolute inset-0 ${t.solid} opacity-90`} />
      )}
      {id && vertical && (
        <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt="" loading="lazy" className="absolute inset-y-0 left-1/2 -translate-x-1/2 h-full object-cover" style={{ aspectRatio: "9/16" }} />
      )}
      {!id && <Icon className="absolute inset-0 m-auto text-white/90" size={40} />}
      <span className="ol-play absolute inset-0 m-auto w-11 h-11 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-lg">
        <FaPlay className="ml-0.5" size={13} />
      </span>
    </div>
  );
};

/* ================= page ================= */

const OwnerLink = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [view, setView] = useState("grid");
  const [checked, setChecked] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE = 12;

  // add panel
  const [addOpen, setAddOpen] = useState(false);
  const [mode, setMode] = useState("single"); // single | bulk
  const [linkType, setLinkType] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);

  const [confirm, setConfirm] = useState(null); // array of links
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const searchRef = useRef(null);
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/videos.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await post("owner-video-list");
      const data = res?.data || res || {};
      const flattened = Array.isArray(data) ? data : Object.values(data).filter(Array.isArray).flat();
      setLinks(flattened);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch owner links" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const existing = useMemo(() => new Set(links.map((l) => normUrl(l.link_url))), [links]);

  const uploadOne = async (type, url) => {
    const res = await post("owner-upload-link", { link_type: type, link_url: url.trim() });
    if (!res?.status) throw new Error(res?.error || "Failed to upload owner link");
    return res;
  };

  /* ----- add single ----- */

  const singleError = linkType && linkUrl ? checkUrl(linkUrl, linkType) : "";
  const singleDuplicate = linkUrl && existing.has(normUrl(linkUrl));

  const onUrlChange = (v) => {
    setLinkUrl(v);
    const guess = detectType(v);
    // Auto-pick only when nothing is chosen or the platform is different (keeps a manual Video/Reel choice)
    if (guess && (!linkType || linkType.split(" ")[0] !== guess.split(" ")[0])) setLinkType(guess);
  };

  const uploadLink = async () => {
    if (!linkType || !linkUrl.trim()) {
      toast.current?.show({ severity: "error", summary: "Validation Error", detail: "Please select link type and enter URL" });
      return;
    }
    if (singleError) {
      toast.current?.show({ severity: "error", summary: "Check the link", detail: singleError });
      return;
    }
    setSubmitting(true);
    try {
      const res = await uploadOne(linkType, linkUrl);
      toast.current?.show({ severity: "success", summary: "Uploaded", detail: res.message || "Owner link uploaded successfully" });
      setLinkUrl("");
      setLinkType("");
      setAddOpen(false);
      fetchLinks();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: err.message });
    }
    setSubmitting(false);
  };

  /* ----- add many ----- */

  const bulkRows = useMemo(() => {
    const seen = new Set();
    return bulkText
      .split(/\s+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((url) => {
        const type = detectType(url);
        const dup = existing.has(normUrl(url)) || seen.has(normUrl(url));
        seen.add(normUrl(url));
        const err = !type ? "Can't tell the type from this link" : checkUrl(url, type) || (dup ? "Already added" : "");
        return { url, type, err };
      });
  }, [bulkText, existing]);

  const uploadBulk = async () => {
    const good = bulkRows.filter((r) => !r.err);
    if (!good.length) return;
    setSubmitting(true);
    const results = [];
    for (const r of good) {
      try {
        await uploadOne(r.type, r.url);
        results.push({ ...r, ok: true });
      } catch (e) {
        results.push({ ...r, ok: false, err: e.message });
      }
      setBulkResults([...results]);
    }
    setSubmitting(false);
    const ok = results.filter((r) => r.ok).length;
    if (ok) {
      toast.current?.show({ severity: "success", summary: "Uploaded", detail: `${ok} link${ok > 1 ? "s" : ""} added` });
      fetchLinks();
    }
    if (ok === good.length) {
      setBulkText("");
      setTimeout(() => setBulkResults([]), 1500);
    }
  };

  /* ----- remove ----- */

  const runRemove = async () => {
    if (!confirm?.length) return;
    setWorking(true);
    let ok = 0;
    for (const l of confirm) {
      try {
        const res = await post("delete-video", { post_id: l.post_id });
        if (res && res.status === false) throw new Error("Failed");
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setChecked([]);
    if (ok) toast.current?.show({ severity: "success", summary: "Removed", detail: ok === 1 ? "This link was removed successfully" : `${ok} links removed` });
    if (ok < confirm.length) toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to remove some links" });
    fetchLinks();
  };

  /* ----- derived ----- */

  const q = query.trim();

  const counts = useMemo(() => {
    const c = { all: links.length };
    TYPES.forEach((t) => {
      c[t.value] = links.filter((l) => l.link_type === t.value).length;
    });
    return c;
  }, [links]);

  const stats = useMemo(() => {
    const views = links.reduce((s, l) => s + Number(l.views_num || 0), 0);
    const karma = links.reduce((s, l) => s + Number(l.point_used || 0), 0);
    return { total: links.length, views, karma, avg: links.length ? Math.round(views / links.length) : 0 };
  }, [links]);

  const maxViews = useMemo(() => Math.max(1, ...links.map((l) => Number(l.views_num || 0))), [links]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    let l = links.filter((x) => {
      if (typeFilter !== "all" && x.link_type !== typeFilter) return false;
      if (!ql) return true;
      return [x.link_url, x.post_id, x.link_type, x.user_id].map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(ql));
    });
    l = [...l].sort((a, b) => {
      if (sortBy === "views") return Number(b.views_num || 0) - Number(a.views_num || 0);
      if (sortBy === "least") return Number(a.views_num || 0) - Number(b.views_num || 0);
      const da = parseAny(a.link_date)?.getTime() || 0;
      const db = parseAny(b.link_date)?.getTime() || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
  }, [links, q, typeFilter, sortBy]);

  useEffect(() => setPage(1), [q, typeFilter, sortBy, view]);
  useEffect(() => {
    const valid = new Set(links.map((l) => l.post_id));
    setChecked((c) => c.filter((id) => valid.has(id)));
  }, [links]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE, safePage * PAGE);
  const allPageChecked = pageRows.length > 0 && pageRows.every((l) => checked.includes(l.post_id));
  const checkedLinks = links.filter((l) => checked.includes(l.post_id));

  const toggleCheck = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const copyText = (text) => {
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: "Copied", detail: "Link copied", life: 1500 });
  };

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (addOpen) {
        if (e.key === "Escape" && !submitting) setAddOpen(false);
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) mode === "single" ? uploadLink() : uploadBulk();
        return;
      }
      if (typing) return;
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "n") {
        e.preventDefault();
        setAddOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* ----- pieces ----- */

  const ViewsBar = ({ l }) => (
    <div className="h-1 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
      <div className="ol-bar h-full rounded-full bg-cyan-500" style={{ width: `${(Number(l.views_num || 0) / maxViews) * 100}%` }} />
    </div>
  );

  const statCards = [
    { label: "Owner links", value: stats.total, icon: FaLayerGroup, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300" },
    { label: "Total views", value: stats.views, icon: FaEye, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300" },
    { label: "Average views", value: stats.avg, icon: FaFire, tint: "bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300" },
    { label: "Karma used", value: stats.karma, icon: FaMagic, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300" },
  ];

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="ol-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Owner Links</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Owner Links</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">YCNP's own videos that users watch in the app to earn karma.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchLinks} disabled={loading} className="ol-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button type="button" onClick={() => setAddOpen(true)} className="ol-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold">
            <FaPlus size={12} /> Add Link
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className={`${CARD} ol-in p-4 flex items-center gap-4`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{fmt(c.value)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Platform tabs */}
      <div className="ol-in mt-4 flex gap-2 overflow-x-auto pb-1" style={{ animationDelay: "240ms" }}>
        {[{ value: "all", short: "All links", icon: FaLayerGroup }, ...TYPES].map((t) => {
          const Icon = t.icon;
          const active = typeFilter === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTypeFilter(t.value)}
              className={`ol-press shrink-0 inline-flex items-center gap-2 h-10 pl-3 pr-2 rounded-xl border text-sm font-medium ${
                active
                  ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                  : "bg-white dark:bg-secondary-dark-bg text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-slate-400"
              }`}
            >
              <Icon className={active ? "" : t.text || ""} />
              {t.short}
              <span className={`min-w-[24px] h-6 px-1.5 rounded-md text-xs font-bold flex items-center justify-center tabular-nums ${active ? "bg-white/20" : "bg-slate-100 dark:bg-slate-700"}`}>
                {counts[t.value] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className={`${CARD} ol-in mt-3 p-3 flex flex-wrap items-center gap-3`} style={{ animationDelay: "280ms" }}>
        <div className="relative flex-1 min-w-[240px]">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by link, post ID or type" className={`${INPUT} w-full h-10 pl-10 pr-10`} />
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {query ? (
              <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                <FaTimes size={12} />
              </button>
            ) : (
              <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
            )}
          </div>
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-10 px-3`}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="views">Most views</option>
          <option value="least">Least views</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            checked={allPageChecked}
            onChange={() =>
              setChecked((c) => (allPageChecked ? c.filter((id) => !pageRows.some((l) => l.post_id === id)) : Array.from(new Set([...c, ...pageRows.map((l) => l.post_id)]))))
            }
            className="w-4 h-4 accent-cyan-600"
          />
          Select page
        </label>
        <div className="inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
          {[
            { id: "grid", icon: FaTh, label: "Grid view" },
            { id: "list", icon: FaList, label: "List view" },
          ].map((v) => {
            const Icon = v.icon;
            return (
              <button key={v.id} type="button" onClick={() => setView(v.id)} aria-label={v.label} title={v.label} className={`w-8 h-7 rounded-md flex items-center justify-center transition-all active:scale-95 ${view === v.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                <Icon size={12} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Bulk bar */}
      {checked.length > 0 && (
        <div className="ol-fade mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 px-4 py-2.5 text-sm">
          <span className="font-medium text-cyan-900 dark:text-cyan-100 mr-2">{checked.length} selected</span>
          <button type="button" onClick={() => setConfirm(checkedLinks)} className="ol-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-medium">
            <FaTrash size={11} /> Remove selected
          </button>
          <button type="button" onClick={() => setChecked([])} className="ml-auto text-slate-600 dark:text-slate-300 hover:underline">
            Clear selection
          </button>
        </div>
      )}

      <p className="mt-4 mb-3 text-sm text-slate-600 dark:text-slate-300">
        {q ? (
          <>
            <b>{filtered.length}</b> result{filtered.length === 1 ? "" : "s"} for "<b>{q}</b>"
          </>
        ) : (
          <>
            <b>{filtered.length}</b> link{filtered.length === 1 ? "" : "s"}
          </>
        )}
      </p>

      {/* Content */}
      {loading && !links.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`${CARD} overflow-hidden`}>
              <div className="bg-slate-100 dark:bg-slate-700 animate-pulse" style={{ aspectRatio: "16/9" }} />
              <div className="p-3 space-y-2">
                <div className="h-3 rounded bg-slate-100 dark:bg-slate-700 animate-pulse" />
                <div className="h-3 w-2/3 rounded bg-slate-100 dark:bg-slate-700 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : pageRows.length === 0 ? (
        <div className={`${CARD} px-5 py-14 text-center`}>
          <FaInbox className="mx-auto text-slate-300" size={34} />
          <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">{q ? `No link found for "${q}"` : links.length ? "No links of this type" : "No owner links yet"}</p>
          <button type="button" onClick={() => setAddOpen(true)} className="ol-press mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 text-white text-sm font-semibold">
            <FaPlus size={12} /> Add Link
          </button>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {pageRows.map((l, i) => {
            const t = typeInfo(l.link_type);
            const Icon = t.icon;
            const d = parseAny(l.link_date);
            const sel = checked.includes(l.post_id);
            return (
              <article
                key={l.post_id}
                className={`ol-card ol-in rounded-xl border bg-white dark:bg-secondary-dark-bg overflow-hidden ${sel ? "border-cyan-500 ring-2 ring-cyan-500/30" : "border-slate-200 dark:border-slate-700"}`}
                style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
              >
                <div className="relative">
                  <a href={l.link_url} target="_blank" rel="noreferrer" title="Open video" className="block">
                    <Thumb link={l} />
                  </a>
                  <label className="absolute top-2 left-2 w-7 h-7 rounded-md bg-white/95 dark:bg-slate-900/80 flex items-center justify-center cursor-pointer shadow-sm">
                    <input type="checkbox" checked={sel} onChange={() => toggleCheck(l.post_id)} className="w-4 h-4 accent-cyan-600" aria-label={`Select ${l.post_id}`} />
                  </label>
                  <span className={`absolute top-2 right-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full text-white ${t.solid}`}>
                    <Icon size={10} /> {t.short}
                  </span>
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded bg-black/70 text-white">
                    <FaClock size={9} /> {fmtWatch(l.watch_time)}
                  </span>
                </div>
                <div className="p-3">
                  <p className="text-xs text-slate-500 truncate" title={l.link_url}>
                    <Hi text={l.link_url} q={q} />
                  </p>
                  <div className="mt-2 flex items-center gap-3 text-sm">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900 dark:text-white tabular-nums">
                      <FaEye className="text-cyan-600" size={12} /> {fmt(l.views_num)}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300 tabular-nums">
                      <FaMagic className="text-violet-500" size={11} /> {fmt(l.point_used)}
                    </span>
                    <span className="ml-auto text-xs text-slate-500">{fmtDate(d)}</span>
                  </div>
                  <div className="mt-2">
                    <ViewsBar l={l} />
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400 tabular-nums">
                    Post <Hi text={l.post_id} q={q} />
                  </p>
                </div>
                <div className="grid grid-cols-3 border-t border-slate-100 dark:border-slate-700 divide-x divide-slate-100 dark:divide-slate-700">
                  <a href={l.link_url} target="_blank" rel="noreferrer" className="h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                    <FaExternalLinkAlt size={10} /> Open
                  </a>
                  <button type="button" onClick={() => copyText(l.link_url)} className="h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 active:bg-slate-100 transition-colors">
                    <FaCopy size={10} /> Copy
                  </button>
                  <button type="button" onClick={() => setConfirm([l])} className="h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 active:bg-rose-100 transition-colors">
                    <FaTrash size={10} /> Remove
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className={`${CARD} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="pl-4 py-3 w-8" />
                  <th className="px-3 py-3 font-medium text-left">Video</th>
                  <th className="px-3 py-3 font-medium text-left">Link Type</th>
                  <th className="px-3 py-3 font-medium text-right whitespace-nowrap">Watch Time</th>
                  <th className="px-3 py-3 font-medium text-right whitespace-nowrap">Karma Used</th>
                  <th className="px-3 py-3 font-medium text-left">Views</th>
                  <th className="px-3 py-3 font-medium text-left whitespace-nowrap">Date Time</th>
                  <th className="px-3 py-3 pr-4 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {pageRows.map((l) => {
                  const t = typeInfo(l.link_type);
                  const Icon = t.icon;
                  const d = parseAny(l.link_date);
                  return (
                    <tr key={l.post_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                      <td className="pl-4 py-3">
                        <input type="checkbox" checked={checked.includes(l.post_id)} onChange={() => toggleCheck(l.post_id)} className="w-4 h-4 accent-cyan-600" aria-label={`Select ${l.post_id}`} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3 min-w-[260px]">
                          <a href={l.link_url} target="_blank" rel="noreferrer" className="ol-card shrink-0 w-28 rounded-md overflow-hidden">
                            <Thumb link={l} />
                          </a>
                          <div className="min-w-0">
                            <a href={l.link_url} target="_blank" rel="noreferrer" className="block max-w-[240px] truncate text-slate-800 dark:text-slate-100 hover:text-cyan-700 hover:underline" title={l.link_url}>
                              <Hi text={l.link_url} q={q} />
                            </a>
                            <p className="text-xs text-slate-500 tabular-nums">
                              Post <Hi text={l.post_id} q={q} />
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${t.bg} ${t.text}`}>
                          <Icon size={11} /> {l.link_type}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtWatch(l.watch_time)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmt(l.point_used)}</td>
                      <td className="px-3 py-3 min-w-[120px]">
                        <p className="font-semibold text-slate-900 dark:text-white tabular-nums">{fmt(l.views_num)}</p>
                        <ViewsBar l={l} />
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                        <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                      </td>
                      <td className="px-3 py-3 pr-4">
                        <div className="flex justify-end gap-1.5">
                          <button type="button" onClick={() => copyText(l.link_url)} title="Copy link" aria-label="Copy link" className="ol-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300">
                            <FaCopy size={11} />
                          </button>
                          <button type="button" onClick={() => setConfirm([l])} className="ol-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white dark:bg-rose-900/30 dark:text-rose-300 text-sm font-medium">
                            <FaTrash size={10} /> Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filtered.length > PAGE && (
        <div className="flex items-center justify-center gap-3 mt-5 text-sm">
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="ol-press w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
            <FaChevronLeft size={11} />
          </button>
          <span className="tabular-nums text-slate-600 dark:text-slate-300">
            Page {safePage} of {totalPages}
          </span>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="ol-press w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Next page">
            <FaChevronRight size={11} />
          </button>
        </div>
      )}

      {/* ================= Add panel ================= */}
      {addOpen && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="ol-fade absolute inset-0 bg-slate-900/40" onClick={() => !submitting && setAddOpen(false)} />
          <aside className="ol-slide relative w-full max-w-lg h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-xs text-slate-500">Owner Links</p>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Add Owner Link</h2>
              </div>
              <button type="button" onClick={() => setAddOpen(false)} disabled={submitting} className="ol-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <div className="px-5 pt-4">
              <div className="inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
                {[
                  { id: "single", label: "One link" },
                  { id: "bulk", label: "Many links at once" },
                ].map((m) => (
                  <button key={m.id} type="button" onClick={() => setMode(m.id)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all active:scale-95 ${mode === m.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {mode === "single" ? (
                <>
                  <div>
                    <label htmlFor="link_url" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Video URL</label>
                    <input
                      id="link_url"
                      value={linkUrl}
                      onChange={(e) => onUrlChange(e.target.value)}
                      placeholder="Paste the full URL of the video"
                      autoFocus
                      className={`${INPUT} w-full h-11 px-3 ${singleError ? "border-rose-300 focus:ring-rose-400" : ""}`}
                    />
                    {singleError ? (
                      <p className="text-xs text-rose-600 mt-1">{singleError}</p>
                    ) : singleDuplicate ? (
                      <p className="text-xs text-amber-600 mt-1">This link is already in the list.</p>
                    ) : (
                      linkUrl && detectType(linkUrl) && <p className="text-xs text-emerald-600 mt-1 inline-flex items-center gap-1"><FaMagic size={10} /> Type detected automatically</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Link Type</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {TYPES.map((t) => {
                        const Icon = t.icon;
                        const active = linkType === t.value;
                        return (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => setLinkType(t.value)}
                            className={`ol-press flex flex-col items-center gap-1 rounded-xl border p-3 text-sm font-medium ${
                              active ? `border-transparent text-white ${t.solid} shadow-md` : "border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:border-slate-400"
                            }`}
                          >
                            <Icon size={20} className={active ? "" : t.text} />
                            {t.value}
                            <span className={`text-[11px] ${active ? "text-white/80" : "text-slate-400"}`}>{t.watch ? `${t.watch}s watch` : "watch time not set"}</span>
                          </button>
                        );
                      })}
                    </div>
                    {linkType === "Facebook Video" && (
                      <p className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-xs p-2.5">
                        <FaExclamationTriangle className="mt-0.5 shrink-0" />
                        The server has no watch time set for Facebook Video, so this upload may fail until the server is fixed.
                      </p>
                    )}
                  </div>

                  {linkUrl && ytId(linkUrl) && (
                    <div className="ol-fade">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Preview</p>
                      <Thumb link={{ link_url: linkUrl, link_type: linkType }} className="rounded-xl" />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <label htmlFor="bulk" className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Paste links, one per line</label>
                    <textarea id="bulk" rows={7} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={"https://youtu.be/...\nhttps://youtube.com/shorts/...\nhttps://www.instagram.com/reel/..."} className={`${INPUT} w-full px-3 py-2.5 font-mono text-xs`} />
                    <p className="text-xs text-slate-500 mt-1">The type of each link is detected automatically. Wrong or repeated links are skipped.</p>
                  </div>
                  {bulkRows.length > 0 && (
                    <ul className="space-y-1.5">
                      {bulkRows.map((r, i) => {
                        const t = typeInfo(r.type);
                        const Icon = t.icon;
                        const res = bulkResults.find((x) => x.url === r.url);
                        return (
                          <li key={i} className={`ol-fade flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${r.err || res?.ok === false ? "border-rose-200 bg-rose-50/60 dark:border-rose-800 dark:bg-rose-900/20" : res?.ok ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-900/20" : "border-slate-200 dark:border-slate-700"}`}>
                            {r.type ? <Icon className={t.text} /> : <FaLink className="text-slate-400" />}
                            <span className="flex-1 truncate text-slate-700 dark:text-slate-200">{r.url}</span>
                            {res?.ok ? (
                              <FaCheckCircle className="text-emerald-500" />
                            ) : r.err || res?.err ? (
                              <span className="inline-flex items-center gap-1 text-rose-600 whitespace-nowrap"><FaTimesCircle /> {res?.err || r.err}</span>
                            ) : (
                              <span className="text-slate-500 whitespace-nowrap">{r.type}</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <span className="hidden sm:inline text-xs text-slate-500">Ctrl + Enter to save</span>
              <div className="flex gap-2 ml-auto">
                <button type="button" onClick={() => setAddOpen(false)} disabled={submitting} className="ol-press h-10 px-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
                  Cancel
                </button>
                {mode === "single" ? (
                  <button type="button" onClick={uploadLink} disabled={submitting || !linkType || !linkUrl.trim() || !!singleError} className="ol-press inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-50">
                    {submitting ? <FaSyncAlt className="animate-spin" size={12} /> : <FaPlus size={11} />}
                    {submitting ? "Saving..." : "Save"}
                  </button>
                ) : (
                  <button type="button" onClick={uploadBulk} disabled={submitting || !bulkRows.some((r) => !r.err)} className="ol-press inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-50">
                    {submitting ? <FaSyncAlt className="animate-spin" size={12} /> : <FaPlus size={11} />}
                    {submitting ? "Adding..." : `Add ${bulkRows.filter((r) => !r.err).length} links`}
                  </button>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Confirm remove */}
      {confirm && confirm.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="ol-fade absolute inset-0 bg-slate-900/50" onClick={() => !working && setConfirm(null)} />
          <div className="ol-zoom relative w-full max-w-md rounded-xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-11 h-11 rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300 flex items-center justify-center">
              <FaExclamationTriangle size={18} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{confirm.length === 1 ? "Remove this owner link?" : `Remove ${confirm.length} owner links?`}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Users will no longer see it in the app. This action cannot be undone.</p>
            <div className="mt-4 max-h-44 overflow-y-auto space-y-2">
              {confirm.slice(0, 6).map((l) => (
                <div key={l.post_id} className="flex items-center gap-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
                  <div className="w-20 shrink-0 rounded overflow-hidden">
                    <Thumb link={l} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-700 dark:text-slate-200 truncate">{l.link_url}</p>
                    <p className="text-[11px] text-slate-500">{fmt(l.views_num)} views</p>
                  </div>
                </div>
              ))}
              {confirm.length > 6 && <p className="text-xs text-slate-500">and {confirm.length - 6} more</p>}
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="ol-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Keep It
              </button>
              <button type="button" onClick={runRemove} disabled={working} className="ol-press flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                {working ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerLink;